import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { GuestService } from '@core/auth/guest.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map, of } from 'rxjs';
import { pickForwardedAuthParams } from './auth.config';

/**
 * Application-wide guard that turns the global `?organization=<slug>` query
 * parameter into an organization-scoped Auth0 login flow.
 *
 * Placed on the root layout route so it applies to every application page
 * regardless of which URL the user lands on. Behaviour:
 * - No `organization` parameter → no-op, the navigation proceeds.
 * - A valid guest session → no-op, a guest access is never hijacked by an
 *   organization login.
 * - Otherwise the authentication state is resolved through `checkAuth()`
 *   (the application performs no global auth check at startup, so this is what
 *   restores the session / refresh token reliably):
 *   - Already authenticated → no-op (we never force an organization switch).
 *   - Not authenticated → trigger `authorize()` forwarding the organization
 *     context to Auth0 and cancel the current navigation.
 *
 * @returns `true` to let the navigation continue, or `false` when an Auth0
 * redirect has been triggered.
 */
export const OrganizationGuard: CanActivateFn = (route) => {
  const oidcSecurityService = inject(OidcSecurityService);
  const guestService = inject(GuestService);

  const customParams = pickForwardedAuthParams(route.queryParams);

  // No organization context in the URL: nothing to do, let other guards run.
  if (!customParams['organization']) {
    return of(true);
  }

  // A valid guest session takes precedence over an organization login.
  if (guestService.hasValidToken()) {
    return of(true);
  }

  return oidcSecurityService.checkAuth().pipe(
    map((loginResponse) => {
      // Already logged in: do nothing (no logout, no organization switch).
      if (loginResponse.isAuthenticated) {
        return true;
      }
      // Not authenticated: start an organization-scoped Auth0 login flow.
      oidcSecurityService.authorize(undefined, { customParams });
      return false;
    }),
  );
};
